import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { ICompany } from '~/client/graph'
import ConfirmDialogTypes from '~/client/src/desktop/enums/ConfirmDialogTypes'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import { toast } from '~/client/src/shared/utils/toast'

import BulkEditCompaniesSideBar from '../BulkEditCompaniesSideBar/BulkEditCompaniesSideBar'
import CompanyDialog from '../CompanyDialog/CompanyDialog'
import ConfirmCompaniesDialog from '../ConfirmCompaniesDialog/ConfirmCompaniesDialog'
import { CompanyMutations } from '../hooks/useCompanyMutations'
import { ModalState } from '../hooks/useModalState'
import { CompanyStatusAction, ICompanyRow } from '../types'

interface ProvidedStores {
  companiesStore: CompaniesStore
  state: DesktopInitialState
}

interface IModalsProps {
  modalState: ModalState
  mutations: CompanyMutations
  selectedRows: readonly ICompanyRow[]
  statusAction: CompanyStatusAction | null
  clearSelection: () => void
}

function toCompanies(
  companiesStore: CompaniesStore,
  companyIds: readonly string[],
): Company[] {
  return companyIds.flatMap(id => {
    const company = companiesStore.getCompanyById(id)
    return company ? [company.asJson as Company] : []
  })
}

function Modals({
  modalState,
  mutations,
  selectedRows,
  statusAction,
  clearSelection,
}: IModalsProps): React.ReactElement {
  const { companiesStore, state } = React.useContext(
    MobXProviderContext,
  ) as unknown as ProvidedStores
  const { companyDialog, statusConfirm, setError } = modalState
  const activeProjectId = state.activeProject?.id

  const selectedCompanies = React.useMemo(
    () =>
      toCompanies(
        companiesStore,
        selectedRows.map(row => row.id),
      ),
    [companiesStore, selectedRows],
  )

  const itemsToEdit = React.useMemo<ICompany[]>(() => {
    if (!companyDialog) return []
    if (companyDialog.mode === 'add') {
      return [Company.getNewCompanyDto(activeProjectId)]
    }
    return toCompanies(companiesStore, companyDialog.companyIds)
  }, [activeProjectId, companiesStore, companyDialog])

  const companiesToConfirm = React.useMemo(
    () =>
      statusConfirm
        ? toCompanies(companiesStore, statusConfirm.companyIds)
        : [],
    [companiesStore, statusConfirm],
  )

  const handleSubmit = async (
    companies: Company[],
    lastUpdatedCompanyId: string,
  ) => {
    if (companyDialog?.mode === 'merge') {
      const result = await mutations.mergeCompanies(
        companies,
        lastUpdatedCompanyId,
      )
      if (result.ok === false) {
        setError(result.errorMessage)
        return
      }
      clearSelection()
      modalState.closeCompanyDialog()
      return
    }

    const result = await mutations.saveCompanies(
      companies,
      companyDialog?.mode === 'add',
    )
    if (result.ok === false) {
      setError(result.errorMessage)
      return
    }
    modalState.closeCompanyDialog()
  }

  const handleBulkSave = async (companies: Company[]) => {
    const result = await mutations.bulkSaveCompanies(companies)
    if (result.ok === false) setError(result.errorMessage)
  }

  const handleToggleStatusConfirm = (isShow: boolean) => {
    if (!isShow) {
      modalState.closeStatusConfirm()
      return
    }
    if (!statusAction) return
    modalState.openStatusConfirm(
      statusAction,
      selectedRows.map(row => row.id),
    )
  }

  const handlePerformStatus = async (companies: Company[]) => {
    const companyIds = companies.map(company => company.id)
    const result =
      statusConfirm?.action === CompanyStatusAction.Activate
        ? await mutations.activateCompanies(companyIds)
        : await mutations.deactivateCompanies(companyIds)
    if (result.ok === false) {
      toast.error(Localization.translator.failedToSaveChangesTryAgain)
      return
    }
    modalState.closeStatusConfirm()
    clearSelection()
  }

  return (
    <>
      {companyDialog && (
        <CompanyDialog
          isMergingMode={companyDialog.mode === 'merge'}
          closeDialog={modalState.closeCompanyDialog}
          loading={mutations.isUpdating}
          onSubmit={handleSubmit}
          itemsToEdit={itemsToEdit}
          allItems={companiesStore.allCompanies}
          errorMessage={modalState.errorMessage}
        />
      )}
      {modalState.isBulkEditOpen && (
        <BulkEditCompaniesSideBar
          selectedCompanies={selectedCompanies}
          loading={mutations.isUpdating}
          toggleDeleteDialog={handleToggleStatusConfirm}
          statusAction={statusAction}
          onClose={modalState.closeBulkEdit}
          saveCompanies={handleBulkSave}
        />
      )}
      <ConfirmCompaniesDialog
        type={ConfirmDialogTypes.DELETE}
        isOpen={!!statusConfirm}
        loading={mutations.isUpdating}
        closeDialog={modalState.closeStatusConfirm}
        performItems={handlePerformStatus}
        itemsToPerform={companiesToConfirm}
        isActivationMode={
          statusConfirm?.action === CompanyStatusAction.Activate
        }
      />
    </>
  )
}

export default observer(Modals)
